/**
 * Ядро аналитики: выбирает провайдеров и раздаёт им вызовы.
 *
 * Зачем слой вообще: RU-инстанс (vsenapole.ru) уезжает на Яндекс.Метрику,
 * AE-инстанс (fixty.io) остаётся на PostHog, а ветка кода одна на оба. Разница
 * между инстансами должна жить в .env, а не в коде — иначе клоны разъедутся,
 * и первый же общий деплой это докажет.
 *
 * ⚠️ Провайдер выбирается по **env, а не по домену**. Домен врёт первым: один
 * образ обслуживает оба домена, а провайдеры переключаются переменными. То же
 * правило и по той же причине действует в backend/services/instanceProfile.js.
 *
 * План миграции — metrika_migration_plan.md в корне репозитория.
 */
import * as posthog from './providers/posthog';
import * as metrika from './providers/metrika';
import * as noop from './providers/noop';
import { attachAutocapture } from './autocapture';

const AVAILABLE = { posthog, metrika };

// REACT_APP_ANALYTICS: posthog | metrika | both | none.
//
// Пусто = 'posthog'. Дефолт именно такой, а не 'none': на момент ввода
// адаптера ни один из двух .env переменной не содержит, и молчаливое
// выключение аналитики на PROD было бы худшим из возможных исходов
// рефакторинга, который не должен менять поведение.
const MODE_PROVIDERS = {
  posthog: ['posthog'],
  metrika: ['metrika'],
  both: ['posthog', 'metrika'],
  none: [],
};

function resolveProviders() {
  const mode = String(process.env.REACT_APP_ANALYTICS || 'posthog').toLowerCase();
  const names = MODE_PROVIDERS[mode];

  if (!names) {
    // Опечатка в .env не должна ронять приложение — но и молчать нельзя,
    // иначе инстанс месяц проработает без аналитики и никто не заметит.
    console.warn(`[analytics] неизвестный REACT_APP_ANALYTICS="${mode}", откат на posthog`);
    return resolveByNames(MODE_PROVIDERS.posthog);
  }

  return resolveByNames(names);
}

function resolveByNames(names) {
  const picked = [];

  for (const name of names) {
    const provider = AVAILABLE[name];
    if (!provider) {
      // Имя режима валидно, а провайдера ещё нет в сборке — так выглядит
      // 'metrika' и 'both' до Фазы 2. Явное предупреждение вместо тихого
      // пропуска: оно и есть напоминание, что фаза не доделана.
      console.warn(`[analytics] провайдер "${name}" не собран, пропущен`);
      continue;
    }
    if (provider.isConfigured()) picked.push(provider);
  }

  return picked.length ? picked : [noop];
}

let providers = [noop];
let initialized = false;

// Вызывается один раз из index.js, до рендера.
export function initAnalytics() {
  if (initialized) return;
  initialized = true;

  providers = resolveProviders();
  providers.forEach((p) => p.init());

  // Автозахват поднимаем после инициализации: до неё неизвестно, кто из
  // провайдеров реально активен, а значит и нужен ли делегат вообще.
  if (needsAutocaptureShim()) attachAutocapture(captureEvent);
}

// Каждый провайдер решает сам, что делать с пользователем: PostHog сбрасывает
// сессию при смене identity, Метрика (Фаза 2) шлёт setUserID + userParams.
// Ядро в эту разницу не лезет.
export function identifyUser(user) {
  if (!user || user.id == null) return;
  providers.forEach((p) => p.identify(user));
}

export function resetUser() {
  providers.forEach((p) => p.reset());
}

export function captureEvent(event, properties = {}) {
  providers.forEach((p) => p.capture(event, properties));
}

// Пейджвью на смену роута. PostHog снимает их сам (capture_pageview), Метрике
// нужен явный hit — иначе в SPA она увидит один визит на всю сессию работы.
// Вызывающий код шлёт всегда и не знает про эту разницу: ядро само отдаёт
// событие только тем провайдерам, у которых своего пейджвью нет. Иначе на
// сборке both каждый переход приехал бы в PostHog дважды.
export function trackPageview(url) {
  providers.forEach((p) => {
    if (!p.hasNativePageviews) p.pageview(url);
  });
}

// Нужен ли собственный делегат автозахвата (Фаза 3). true, только если хотя бы
// одному активному провайдеру своего автозахвата не хватает; сам делегат при
// этом обязан слать событие через captureEvent, а не мимо.
export function needsAutocaptureShim() {
  return providers.some((p) => !p.hasNativeAutocapture);
}

// Для диагностики: какие провайдеры реально активны в этой сборке.
export function activeProviders() {
  return providers.map((p) => p.key);
}
